/**
 * CotizacionFormDialog - Formulario de creación de cotizaciones
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, X, TestTubes } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { useApiError } from "@/hooks/use-api-error";
import { useBuscarPacientes } from "@/hooks/use-pacientes";
import {
  useCreateCotizacion,
  useBuscarExamenesTipo,
  type ExamenTipoListItem,
} from "@/hooks/use-ordenes";
import { useSucursal } from "@/contexts/sucursal-context";
import { useDebounce } from "@/hooks/use-debounce";

interface CotizacionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CotizacionFormDialog({ open, onOpenChange, onSuccess }: CotizacionFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva } = useSucursal();

  const [pacienteId, setPacienteId] = useState<number | null>(null);
  const [pacienteNombre, setPacienteNombre] = useState("");
  const [selectedExamenes, setSelectedExamenes] = useState<ExamenTipoListItem[]>([]);
  const [diasVigencia, setDiasVigencia] = useState(30);
  const [observaciones, setObservaciones] = useState("");
  // Search state
  const [pacienteSearch, setPacienteSearch] = useState("");
  const [examenSearch, setExamenSearch] = useState("");
  const debouncedPacienteSearch = useDebounce(pacienteSearch, 300);
  const debouncedExamenSearch = useDebounce(examenSearch, 300);

  const { data: pacientesResults } = useBuscarPacientes(debouncedPacienteSearch, {
    enabled: debouncedPacienteSearch.length >= 2 && !pacienteId,
  });
  const { data: examenesResults } = useBuscarExamenesTipo(debouncedExamenSearch, {
    enabled: debouncedExamenSearch.length >= 2,
  });

  const createMutation = useCreateCotizacion({
    onSuccess: () => onSuccess(),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  useEffect(() => {
    if (open) {
      setPacienteId(null);
      setPacienteNombre("");
      setSelectedExamenes([]);
      setDiasVigencia(30);
      setObservaciones("");
      setPacienteSearch("");
      setExamenSearch("");
    }
  }, [open]);

  const handleSelectPaciente = useCallback((id: number, nombre: string) => {
    setPacienteId(id);
    setPacienteNombre(nombre);
    setPacienteSearch("");
  }, []);

  const handleAddExamen = useCallback((examen: ExamenTipoListItem) => {
    setSelectedExamenes((prev) => {
      if (prev.some((e) => e.id === examen.id)) return prev;
      return [...prev, examen];
    });
    setExamenSearch("");
  }, []);

  const handleRemoveExamen = useCallback((id: number) => {
    setSelectedExamenes((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const handleSubmit = () => {
    if (selectedExamenes.length === 0) {
      toast.error(t("ordenes.mustSelectExam"));
      return;
    }
    createMutation.mutate({
      sucursal: sucursalActiva?.id || 0,
      paciente: pacienteId,
      examenes_ids: selectedExamenes.map((e) => e.id),
      dias_vigencia: diasVigencia,
      observaciones,
    });
  };

  const filteredExamResults = examenesResults?.filter(
    (e) => !selectedExamenes.some((s) => s.id === e.id)
  );
  const totalPrecio = selectedExamenes.reduce((sum, e) => sum + Number(e.precio), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("ordenes.cotizaciones.newQuote")}</DialogTitle>
          <DialogDescription>{t("ordenes.cotizaciones.newQuoteDesc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Paciente selector (opcional) */}
          <div className="space-y-2">
            <Label>
              {t("ordenes.fields.patient")} ({t("common.optional")})
            </Label>
            {pacienteId ? (
              <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                <span className="flex-1 font-medium">{pacienteNombre}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    setPacienteId(null);
                    setPacienteNombre("");
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t("ordenes.searchPatient")}
                    value={pacienteSearch}
                    onChange={(e) => setPacienteSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {pacientesResults && pacientesResults.length > 0 && (
                  <div className="border rounded-md max-h-40 overflow-y-auto">
                    {pacientesResults.map((p) => (
                      <button
                        key={p.id}
                        className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                        onClick={() => handleSelectPaciente(p.id, p.nombre_completo)}
                      >
                        <span className="font-medium">{p.nombre_completo}</span>
                        <span className="text-muted-foreground font-mono">{p.identificacion}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Días de vigencia */}
          <div className="space-y-2">
            <Label>{t("ordenes.cotizaciones.fields.daysValid")}</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={diasVigencia}
              onChange={(e) => setDiasVigencia(Number(e.target.value))}
            />
          </div>

          <Separator />

          {/* Exámenes selector */}
          <div className="space-y-2">
            <Label>{t("ordenes.fields.exams")} *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("ordenes.searchExams")}
                value={examenSearch}
                onChange={(e) => setExamenSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {filteredExamResults && filteredExamResults.length > 0 && (
              <div className="border rounded-md max-h-40 overflow-y-auto">
                {filteredExamResults.map((e) => (
                  <button
                    key={e.id}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center"
                    onClick={() => handleAddExamen(e)}
                  >
                    <div>
                      <span className="font-mono text-xs text-muted-foreground mr-2">
                        {e.codigo}
                      </span>
                      <span>{e.nombre}</span>
                    </div>
                    <span className="text-muted-foreground">L {Number(e.precio).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected exams */}
          {selectedExamenes.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  {t("ordenes.selectedExams")} ({selectedExamenes.length})
                </Label>
                <span className="text-sm font-medium">L {totalPrecio.toFixed(2)}</span>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {selectedExamenes.map((e) => (
                  <div key={e.id} className="p-2.5 border rounded-lg text-sm bg-muted/30 space-y-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <TestTubes className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                        {e.codigo}
                      </span>
                      <span className="truncate">{e.nombre}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 pl-5">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {e.area_display}
                      </Badge>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground tabular-nums">
                          L {Number(e.precio).toFixed(2)}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleRemoveExamen(e.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t("ordenes.noExamsSelected")}
            </p>
          )}

          {/* Observaciones */}
          <div className="space-y-2">
            <Label>{t("ordenes.fields.observations")}</Label>
            <Textarea
              placeholder={t("ordenes.fields.observationsPlaceholder")}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedExamenes.length === 0 || createMutation.isPending}
          >
            {createMutation.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
